/**
 * useSidebarSearch — debounced backend search over chats + projects.
 *
 * 500ms debounce after the user stops typing. Short queries (< 2 chars)
 * clear results without hitting the backend. The service layer is the
 * only place that talks to `apiClient` — the component uses the hook.
 */

import { useCallback, useRef, useState } from 'react';
import { searchChats, searchChatsByUrl } from '../../../modules/chat/chatMetadataService';

export type SearchResult = {
    id: string;
    name: string;
    type: 'project' | 'chat';
    project?: string;
    project_name?: string;
};

type SearchPage = {
    results: SearchResult[];
    next: string | null;
};

export function useSidebarSearch() {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [apiSearchResults, setApiSearchResults] = useState<SearchPage | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchChange = useCallback((query: string) => {
        setSearchQuery(query);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim() || query.trim().length < 2) {
            setApiSearchResults(null);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const page = await searchChats(query);
                setApiSearchResults(page as unknown as SearchPage);
            } catch {
                setApiSearchResults(null);
            }
            setIsSearching(false);
        }, 500);
    }, []);

    const loadMore = useCallback(async () => {
        if (!apiSearchResults?.next || isSearching) return;
        setIsSearching(true);
        try {
            const page = await searchChatsByUrl(apiSearchResults.next);
            setApiSearchResults((prev) => prev
                ? { results: [...prev.results, ...(page.results as unknown as SearchResult[])], next: page.next }
                : (page as unknown as SearchPage));
        } catch {
            /* non-blocking — leave already-loaded results */
        }
        setIsSearching(false);
    }, [apiSearchResults, isSearching]);

    const close = useCallback(() => {
        setSearchOpen(false);
        setSearchQuery('');
        setApiSearchResults(null);
    }, []);

    return {
        searchQuery,
        setSearchQuery,
        searchOpen,
        setSearchOpen,
        apiSearchResults,
        isSearching,
        handleSearchChange,
        loadMore,
        close,
    };
}
